import { Controller, type FieldValues } from 'react-hook-form';

import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { cn } from '@/lib/utils';
import { type CommonFormFieldProps, lgSpan, mdSpan, smSpan } from './form-input';

type Option = {
  label: string;
  value: string | number;
};

type FormSelectProps<T extends FieldValues> = CommonFormFieldProps<T> & {
  options: Option[];
  placeholder?: string;
};

export function FormSelect<T extends FieldValues>({
  name,
  control,
  label,
  options,
  placeholder = 'Selecionar',

  sm = 12,
  md = 4,
  lg = 4,

  required,
  className,
}: FormSelectProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field
          className={cn(smSpan[sm], mdSpan[md], lgSpan[lg], className)}
          data-invalid={fieldState.invalid}
        >
          <FieldLabel htmlFor={name}>
            {label} {required && <strong className="text-red-500">*</strong>}
          </FieldLabel>

          <Select
            disabled={field.disabled}
            onValueChange={(value) => {
              if (value) field.onChange(value);
            }}
            value={field.value ? field.value.toString() : ''}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>

            <SelectContent>
              <SelectGroup>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {fieldState.error && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
